import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlbumPage } from './components/AlbumPage';
import { CollectionPage } from './components/CollectionPage';
import { EntityPage } from './components/EntityPage';
import { ExplorePage } from './components/ExplorePage';
import { PickPage } from './components/PickPage';
import { Shell } from './components/Shell';
import { StatsPage } from './components/StatsPage';
import { defaultAlbumFilters } from './domain/library';
import { albumPath, entityPath, parseAppPath, type EntityType } from './domain/routes';
import type { AlbumFilters, CollectionPreferences, LibraryPayload, LibraryStats, SearchDocument } from './types';
import { updatePageMetadata } from './utils/pageMetadata';
import { filtersFromSearch, filtersToSearch } from './utils/filterUrl';

export default function App() {
  const [route, setRoute] = useState(() => parseAppPath(window.location.pathname));
  const [library, setLibrary] = useState<LibraryPayload | null>(null);
  const [stats, setStats] = useState<LibraryStats | null>(null);
  const [searchIndex, setSearchIndex] = useState<SearchDocument[]>([]);
  const [filters, setFilterState] = useState<AlbumFilters>(() => ({ ...defaultAlbumFilters(), ...filtersFromSearch(window.location.search) }));
  const [collectionPreferences, setCollectionPreferences] = useState<CollectionPreferences>({ sort: 'release-desc', view: 'grid', groupBy: 'artist' });
  const [exploreQuery, setExploreQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const pendingScroll = useRef<number | null>(null);
  const collectionPath = useRef(`/collection${filtersToSearch(filters)}`);
  const focusMain = useRef(false);

  const setFilters = useCallback((next: Partial<AlbumFilters>) => setFilterState((current) => ({ ...current, ...next })), []);

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      fetch('/albums.json', { signal: controller.signal }).then((response) => { if (!response.ok) throw new Error('Unable to load the library'); return response.json() as Promise<LibraryPayload>; }),
      fetch('/stats.json', { signal: controller.signal }).then((response) => { if (!response.ok) throw new Error('Unable to load library statistics'); return response.json() as Promise<LibraryStats>; }),
    ]).then(([nextLibrary, nextStats]) => { setLibrary(nextLibrary); setStats(nextStats); }).catch((reason: Error) => { if (reason.name !== 'AbortError') setError(reason.message); });
    // Track titles only refine search, so the larger index loads without delaying the first render.
    fetch('/search-index.json', { signal: controller.signal }).then((response) => response.ok ? response.json() as Promise<SearchDocument[]> : []).then(setSearchIndex).catch(() => undefined);
    return () => controller.abort();
  }, []);

  useEffect(() => {
    // The app restores scroll itself once restored pages have rendered their content.
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    if (typeof window.history.state?.depth !== 'number') window.history.replaceState({ ...window.history.state, depth: 0 }, '');
  }, []);

  const applyLocation = useCallback((event?: PopStateEvent) => {
    const next = parseAppPath(window.location.pathname);
    if (next.page === 'collection' || next.page === 'pick') setFilterState({ ...defaultAlbumFilters(), ...filtersFromSearch(window.location.search) });
    focusMain.current = true;
    pendingScroll.current = typeof event?.state?.scrollY === 'number' ? event.state.scrollY : 0;
    setRoute(next);
  }, []);
  useEffect(() => { window.addEventListener('popstate', applyLocation); return () => window.removeEventListener('popstate', applyLocation); }, [applyLocation]);

  const navigate = useCallback((target: string) => {
    const url = new URL(target, window.location.origin);
    if (window.location.pathname + window.location.search !== url.pathname + url.search) {
      const depth = typeof window.history.state?.depth === 'number' ? window.history.state.depth : 0;
      window.history.replaceState({ ...window.history.state, scrollY: window.scrollY }, '');
      window.history.pushState({ depth: depth + 1 }, '', url.pathname + url.search);
    }
    if (url.pathname === '/collection' || url.pathname === '/pick') setFilterState({ ...defaultAlbumFilters(), ...filtersFromSearch(url.search) });
    focusMain.current = true;
    setRoute(parseAppPath(url.pathname));
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // In-app history returns visitors to where they were; a direct visit falls back to the collection.
  const canGoBack = typeof window.history.state?.depth === 'number' && window.history.state.depth > 0;
  const goBack = useCallback(() => {
    if (typeof window.history.state?.depth === 'number' && window.history.state.depth > 0) window.history.back();
    else navigate(collectionPath.current);
  }, [navigate]);

  useEffect(() => {
    if (route.page === 'collection' || route.page === 'pick') {
      const next = `/${route.page}${filtersToSearch(filters)}`;
      if (route.page === 'collection') collectionPath.current = next;
      if (window.location.pathname + window.location.search !== next) window.history.replaceState(window.history.state, '', next);
    }
  }, [filters, route.page]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      const typing = target.matches('input, textarea, select, [contenteditable="true"]');
      if (((event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase() === 'k') || (event.key === '/' && !typing)) {
        event.preventDefault();
        if (route.page !== 'explore' && route.page !== 'collection') navigate('/');
        window.setTimeout(() => document.querySelector<HTMLInputElement>('[data-global-search]')?.focus(), 0);
      }
      if (event.key === 'Escape' && route.page === 'album' && !(event.target as HTMLElement).closest('dialog, .global-search')) goBack();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [goBack, navigate, route.page]);

  const albums = useMemo(() => library?.albums.filter((album) => album.available !== false) || [], [library]);

  useEffect(() => {
    if (!library) return;
    updatePageMetadata(route, albums);
    // Outdated album slugs are replaced with the canonical URL, matching the server redirect.
    if (route.page === 'album') {
      const album = albums.find((item) => item.id === route.id);
      if (album && window.location.pathname !== albumPath(album)) window.history.replaceState(window.history.state, '', albumPath(album) + window.location.search);
    }
    if (focusMain.current) { document.querySelector<HTMLElement>('main')?.focus({ preventScroll: true }); focusMain.current = false; }
    if (pendingScroll.current !== null) {
      const top = pendingScroll.current;
      pendingScroll.current = null;
      window.scrollTo({ top, behavior: 'instant' });
    }
  }, [route, albums, library]);

  if (error) return <div className="fatal-state" role="alert"><p className="kicker">Library unavailable</p><h1>{error}</h1><button onClick={() => window.location.reload()}>Try again</button></div>;
  if (!library || !stats) return <div className="app-loading"><div className="loading-word">flac.banast.as</div><span>Indexing the signal</span></div>;

  const openEntity = (type: EntityType, value: string) => navigate(entityPath(type, value));
  let content;
  if (route.page === 'explore') content = <ExplorePage albums={albums} index={searchIndex} query={exploreQuery} onQueryChange={setExploreQuery} onNavigate={navigate} onEntityClick={openEntity} />;
  else if (route.page === 'collection') content = <CollectionPage preferences={collectionPreferences} onPreferencesChange={setCollectionPreferences} albums={albums} index={searchIndex} filters={filters} onFiltersChange={setFilters} onNavigate={navigate} onEntityClick={openEntity} onPick={() => navigate(`/pick${filtersToSearch(filters)}`)} />;
  else if (route.page === 'pick') content = <PickPage albums={albums} index={searchIndex} filters={filters} onFiltersChange={setFilters} onNavigate={navigate} onEntityClick={openEntity} />;
  else if (route.page === 'stats') content = <StatsPage onNavigate={navigate} stats={stats} />;
  else if (route.page === 'album') content = <AlbumPage key={route.id} collectionPath={collectionPath.current} canGoBack={canGoBack} onBack={goBack} id={route.id} albums={albums} onNavigate={navigate} onEntityClick={openEntity} />;
  else if (route.page === 'entity') content = <EntityPage type={route.entityType} slug={route.slug} albums={albums} onNavigate={navigate} onEntityClick={openEntity} />;
  else content = <div className="empty-state"><h1>Page not found</h1><p>This path is not represented in the current library.</p><button className="button button--primary" onClick={() => navigate('/')}>Explore the library</button></div>;

  return <Shell collectionPath={route.page === 'collection' ? `/collection${filtersToSearch(filters)}` : collectionPath.current} active={route.page} stats={stats} generatedAt={library.generatedAt} onNavigate={navigate}>{content}</Shell>;
}
